import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface BookShipmentNavigationFooter2Props {
  next?: string | undefined;
  onPrevious?: (() => Promise<void>) | undefined;
  nextUrl?: string | undefined;
  nextOnClick?: (() => void) | undefined;
  nextIsValid: () => Promise<boolean> | boolean;
  nextIsEnabled: () => boolean;
  nextIdentifier: string;
  hidePreviousButton?: boolean | undefined;
}

const BookShipmentNavigationFooter2: React.FC<
  BookShipmentNavigationFooter2Props
> = (props: BookShipmentNavigationFooter2Props) => {
  const navigate = useNavigate();
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function shouldShowNextButton() {
    return (props.nextUrl || props.nextOnClick) && props.next;
  }

  function shouldShowPreviousButton() {
    return !props.hidePreviousButton;
  }

  async function onNext() {
    if (!props.nextIsEnabled() || isLoading) {
      return;
    }

    setIsLoading(true);
    setShowErrorBanner(false);

    try {
      const isValid = await props.nextIsValid();

      if (!isValid) {
        console.error(
          "[BookShipmentNavigationFooter2] Validation failed for:",
          props.nextIdentifier,
        );
        setShowErrorBanner(true);
        setIsLoading(false);
        return;
      }

      if (props.nextOnClick) {
        props.nextOnClick();
        setIsLoading(false);
      } else if (props.nextUrl) {
        navigate(props.nextUrl);
      }
    } catch (error) {
      console.error(
        "[BookShipmentNavigationFooter2] Exception during validation:",
        error,
      );
      setIsLoading(false);
      setShowErrorBanner(true);
    }
  }

  return (
    // Full width of the screen column, so the buttons finish where the panels
    // above them finish. Capped at 608px they stopped 200px short of that edge,
    // with Previous stranded far away on the left.
    <div className="w-full">
      <Stack gap={4} align="stretch">
        {showErrorBanner && (
          // The banner keeps a readable measure — only the buttons run out to
          // the column edge.
          <div className="max-w-[608px]">
            <Banner variant="danger">Please fix the errors above</Banner>
          </div>
        )}
        <Row gap={4} justify="end">
          {shouldShowPreviousButton() && (
            // Borderless text beside the large primary: two boxed buttons of
            // different sizes read as a mistake, where plain text next to a
            // heavy CTA reads as the hierarchy it is. Same pairing the details
            // screen uses for "Start Again".
            <Button size="link" previousArrow onClick={props.onPrevious}>
              Previous
            </Button>
          )}

          {shouldShowNextButton() && (
            // `large` so the step's primary action reads as the heavier of the
            // two — Previous stays the modest default beside it.
            <Button
              primary
              size="large"
              nextArrow
              onClick={onNext}
              disabled={!props.nextIsEnabled() || isLoading}
              disabledMessage="Complete the required fields above to continue"
              loading={isLoading}
              data-testid="next-button"
            >
              {props.next || ""}
            </Button>
          )}
        </Row>
      </Stack>
    </div>
  );
};
export default BookShipmentNavigationFooter2;
